import type {
  AddressDto,
  CallOutcomeDto,
  ClientDto,
  CoordsDto,
  EmailAddressDto,
  ExternalItemTypeDto,
  MemberProgressedDto,
  PhoneNumberDto,
  UserAppSettingsDto,
  VisitOutcomeDto,
} from '..';

import z from 'zod';
import type { Permission } from '../../../authorization';
import { Application, UserRole } from '../../../definitions';
import type { MemberSegmentDto } from './member-segment.dto';
import type { MemberStatusChangeRequestDto } from './member-status-change-request.dto';
import type { MemberSummaryDto } from './member-summary.dto';
import type { StandardMemberAttributesDto } from './standard-member-attributes.dto';

export interface UserDataViewDto {
  users: UserDto[];
  memberStatusChangeRequests: MemberStatusChangeRequestDto[];
}

export interface UserDto {
  id: string;
  interactableId: string;
  clientId: number;
  clientUserId: string;
  avonId?: string;
  name: string;
  firstName: string;
  lastName: string;
  addresses: AddressDto[];
  emailAddresses: EmailAddressDto[];
  phoneNumbers: PhoneNumberDto[];
  email: string;
  memberEmail: string;
  gender: string;
  preferredName: string;
  namePronunciation: string;
  pronouns: string;
  dateOfBirth: string;
  role: UserRole;
  avatar?: string;
  apps: Application[];
  assignedGuideId: string;
  assignedGuide?: string;
  cadence: string;
  raceEthnicitySummary: string;
  reportedLanguage: string;
  monthlyOutreach: number;

  lastMessageSentAt: number;
  voicemailGreetingFileId: string;

  hasNew: boolean;
  hasAppAccess: boolean;
  lastEventAt: number;
  lastEventType: string;
  nextEventDate: string;
  nextEventTime: number;
  nextEventType: string;
  canBill: boolean;

  memberStatusChangeRequests: MemberStatusChangeRequestDto[];
  memberStatus: UserMemberStatusDto;
  memberStage: MemberStageDto;

  standardMemberAttributes: StandardMemberAttributesDto;
  memberSummary: MemberSummaryDto;

  memberSegments: MemberSegmentDto[];

  isDeleted: boolean;
  createdAt: number;
  updatedAt: number;
  dtoBonus: number;

  userPermissions: Permission[];
  memberPermissions: Permission[];
  showHospitalizations: boolean;
  appSettings?: UserAppSettingsDto;
  memberProgressed?: MemberProgressedDto;
}

export interface UserForGeoMapDto {
  id: string;
  firstName: string;
  lastName: string;
  memberStatus: UserMemberStatusDto;
  coords: CoordsDto;
}

export interface PaginatedUsersDto {
  users: UserDto[];
  count: number;
}

export function isUserForGeo(user) {
  return (
    'id' in user &&
    'firstName' in user &&
    'lastName' in user &&
    'memberStatus' in user &&
    'coords' in user &&
    'memberStatus' in user
  );
}

export interface UserReecapDto {
  callsThisYear: number;
  membersServedThisYear: number;
  needsAddressedThisYear: number;
  needsIdentifiedThisYear: number;
  pcpConnectionsThisYear: number;
  textsThisYear: number;
  visitsThisYear: number;
  totalMembersServed: number;
}

export interface ActingUserDto extends UserDto {
  featureFlags: { [key: string]: boolean };
  permissions: { [key: string]: true };
  reecap?: UserReecapDto;
}

export interface AddressCreationRequestDto {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  primary?: boolean;
  description?: string;
}

export interface UpdateAddressRequestDto {
  primary: boolean;
  description: string;
}

export interface AddressCreationRequestDto {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  primary?: boolean;
  description?: string;
}

export interface UpdatePhoneNumberRequestDto {
  primary: boolean;
  description: string;
}

export interface PhoneNumberCreationRequestDto {
  number?: string;
  primary?: boolean;
  description?: string;
}

export interface UpdateUserSummaryRequestDto {
  text: string;
  isAutoUpdate: boolean;
}

export interface MemberStatusSelectOption {
  isSelectable: boolean;
  memberStatus: MemberStatusDto;
}
export interface MemberStatusDto {
  id: number;
  key: string;
  name: string;
  description: string;
  sortOrder?: number;

  effectiveAtQuestionText?: string;
  canRequestChangeTo?: boolean;
  changeRequestDescription?: string;
  changeRequestStageIds: number[];
  changeRequestStatusIds: number[];
}
export interface UserMemberStatusDto extends MemberStatusDto {
  memberId: string;
  memberStatusId: number;
  memberStatusChangeRequestId: number;
  interactionId: number;
  externalItemId: number;
  externalItemType: ExternalItemTypeDto;
  createdAt: number;
  effectiveAt: number;
}

export type StageKeyDto =
  | 'outreach'
  | 'support'
  | 'skill-building'
  | 'independence';

export interface MemberStageDto {
  id: number;
  key: StageKeyDto;
  name: string;
  description: string;
}

export interface MemberFiltersDto {
  events?: string[];
  statuses?: number[];
}

export interface ContactInfoType {
  address: 'address';
  phoneNumber: 'phoneNumber';
}

export interface TeamDto {
  id: number;
  name: string;
  userIds: string[];
}

export interface TeamHierarchyDto {
  id: number;
  name: string;
  teams: TeamDto[];
}

export interface VerifiedResponseDto {
  user: ActingUserDto;
  authenticated: boolean;
  voiceCallOutcomes: CallOutcomeDto[];
  visitOutcomes: VisitOutcomeDto[];
  memberStatuses: MemberStatusDto[];
  clients: ClientDto[];
}

export interface UpdateUserRequestDto {
  assignedGuideId?: string;
  cadence?: string;
  memberStageId?: number;
  namePronunciation?: string;
  preferredName?: string;
  pronouns?: string;
  reportedLanguage?: string;
  raceEthnicitySummary?: string;
}

export const UpdatePersonalInformationSchema = z.object({
  namePronunciation: z.string().optional(),
  preferredName: z.string().optional(),
  pronouns: z.string().optional(),
  userId: z.string().optional(),
  race: z.string().optional(),
  ethnicity: z.string().optional(),
  language: z.string().optional(),
});
